import { useQuery } from '@apollo/client';
import { BreadcrumbListItem, Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { VISUAL_TESTING_BREADCRUMB } from '../global/constants';
import { graphql, useFragment } from '../graphql/__generated__';
import { BuildStatus, DiffFilter, DiffsOrderBy, DiffStatus } from './../graphql/__generated__/graphql';
import { VisualBuildDetailsPageParams, urls } from '../routes';
import Diff from './diff/Diff';
import { diffSortKey, diffSortValues, isOrderValid, orderDefault } from './diff/DiffHeaderSort';
import DiffNoData from './diff/DiffNoData';

export const BuildFrag = graphql(`
  fragment BuildFrag on Build {
    id
    name
    status
    error
    project
    approved: diffCount(status: APPROVED)
    equal: diffCount(status: EQUAL)
    rejected: diffCount(status: REJECTED)
    unapproved: diffCount(status: UNAPPROVED)
    diffIds: diffs(orderBy: $order, filter: $filter) {
      nodes {
        id
      }
      totalCount
    }
  }
`);

const ReadBuildDocument = graphql(/* GraphQL */ `
  query ReadBuildDocument ($id: UUID!, $order: [DiffsOrderBy!], $filter: DiffFilter!) {
    build(id: $id) {
      ...BuildFrag
    }
  }
`);

export type DiffQueries = {
  order: DiffsOrderBy
}

export const filterDiffNotQueued = {
  status: {
    in: Object.values(DiffStatus).filter((value) => value !== DiffStatus.Queued),
  },
} as DiffFilter;

const BUILD_STATUS_POLL_INTERVAL = 30 * 1000; // 30 seconds

const VisualBuildDetailsPage: React.FC = () => {
  const { search } = useLocation<DiffQueries>();
  const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);
  const orderQuery = queryParams.get('order');
  const orderBackup = orderQuery || localStorage.getItem(diffSortKey);
  const order = orderBackup && isOrderValid(orderBackup) ? orderBackup : orderDefault;
  const breadcrumbs: BreadcrumbListItem[] = useMemo(() => ([
    { name: VISUAL_TESTING_BREADCRUMB },
    { name: 'Builds', url: urls.getBuilds() },
  ]), []);

  const history = useHistory();
  const { buildId, stepId } = useParams<VisualBuildDetailsPageParams>();

  const buildResult = useQuery(ReadBuildDocument, {
    variables: { id: buildId, order: diffSortValues[order], filter: filterDiffNotQueued },
  });
  const build = useFragment(BuildFrag, buildResult.data?.build);
  const isRunning = build?.status === BuildStatus.Running;

  const replaceToDiff = (diffId?: string) => history.replace(
    urls.getDiff(
      buildId,
      diffId,
      { ...queryParams, order },
    ),
  );

  useEffect(() => {
    if (!build?.diffIds.totalCount) {
      return;
    }

    if (isOrderValid(order)) {
      localStorage.setItem(diffSortKey, order);
    }

    if (orderQuery !== order) {
      replaceToDiff(stepId || build?.diffIds.nodes[0].id);
    }
  }, [order]);

  useEffect(() => {
    if (!build?.diffIds.totalCount) {
      return;
    }

    const isStepIdInvalid = !build?.diffIds.nodes.find((node) => node.id === stepId);

    if (isStepIdInvalid) {
      replaceToDiff(build?.diffIds.nodes[0].id);
    }
  }, [stepId, build?.diffIds.nodes[0]?.id]);

  const { startPolling, stopPolling } = buildResult;
  // Automatically poll if buildstatus=running. We'll refresh the results automatically and
  // stop polling if the status changes.
  useEffect(() => {
    if (isRunning) {
      startPolling(BUILD_STATUS_POLL_INTERVAL);
    } else {
      stopPolling();
    }
  }, [isRunning, startPolling, stopPolling]);

  return (
    <>
      <Helmet>
        <title>
          {build?.name ? `Build ${build?.name} | ` : ''}
          Visual
        </title>
      </Helmet>
      <Breadcrumbs list={[...breadcrumbs, { name: build?.name || 'Build' }]} />
      <ContentLoader
        show={(buildResult.loading && !buildResult.data) ||
          (!stepId && !!build?.diffIds.totalCount)}
      >
        {!build?.diffIds.totalCount ?
          <DiffNoData isRunning={isRunning} /> : (
            <Diff
              buildResult={buildResult}
              order={order}
            />
          )}
      </ContentLoader>
    </>
  );
};

export default VisualBuildDetailsPage;
